import type { Dispatch, SetStateAction } from 'react'
import { Coverage, DocumentReference } from '@medplum/fhirtypes'
import { InsuranceCardSide, InsuranceCardReferenceMap } from '../types/insuranceTypes'
import {
  COVERAGE_INSURANCE_CARD_EXTENSION_URLS,
  INSURANCE_CARD_SIDES,
} from '../constants/insuranceConstants'

export type InsuranceCardPreviewSource = File | string | null

export const updateInsuranceCardPreview = (
  setPreview: Dispatch<SetStateAction<string | null>>,
  source: InsuranceCardPreviewSource,
): void => {
  setPreview((current) => {
    if (current?.startsWith('blob:')) {
      URL.revokeObjectURL(current)
    }

    if (!source) {
      return null
    }

    if (typeof source === 'string') {
      return source
    }

    return URL.createObjectURL(source)
  })
}

export const getNextInsuranceCardCaptureSide = (
  frontSource: InsuranceCardPreviewSource,
  backSource: InsuranceCardPreviewSource,
): InsuranceCardSide => {
  if (!frontSource) {
    return 'front'
  }

  if (!backSource) {
    return 'back'
  }

  return 'front'
}

export const extractCoverageInsuranceCardReferences = (
  coverage: Coverage,
): InsuranceCardReferenceMap => {
  const references: InsuranceCardReferenceMap = {}
  const extensions = coverage.extension ?? []
  INSURANCE_CARD_SIDES.forEach((side) => {
    const url = COVERAGE_INSURANCE_CARD_EXTENSION_URLS[side]
    const reference = extensions.find((ext) => ext.url === url)?.valueReference?.reference
    if (reference) {
      references[side] = reference
    }
  })
  return references
}

export const getResourceIdFromReference = (reference?: string): string | undefined => {
  if (!reference) return undefined
  const [resourceType, id] = reference.split('/')
  if (resourceType && id) return id
  return reference
}

export const normalizeDocumentReference = (reference: string): string => {
  if (!reference) return reference
  return reference.startsWith('DocumentReference/') ? reference : `DocumentReference/${reference}`
}

export const getDocumentReferencePointer = (doc: DocumentReference): string | undefined => {
  if (!doc.id) return undefined
  return `DocumentReference/${doc.id}`
}

export const applyCoverageInsuranceCardReferences = (
  coverage: Coverage,
  updates: InsuranceCardReferenceMap,
): Coverage => {
  const updateEntries = Object.entries(updates).filter(([, reference]) => Boolean(reference)) as [
    InsuranceCardSide,
    string,
  ][]

  if (!updateEntries.length) {
    return coverage
  }

  const urlsToReplace = updateEntries.map(([side]) => COVERAGE_INSURANCE_CARD_EXTENSION_URLS[side])
  const preservedExtensions = (coverage.extension ?? []).filter(
    (ext) => !urlsToReplace.includes(ext.url),
  )

  const additions = updateEntries.map(([side, reference]) => ({
    url: COVERAGE_INSURANCE_CARD_EXTENSION_URLS[side],
    valueReference: {
      reference: normalizeDocumentReference(reference),
    },
  }))

  return {
    ...coverage,
    extension: [...preservedExtensions, ...additions],
  }
}
